import React from "react";
import { Briefcase, Mail, Kanban, FileSignature, Sparkles, BookMarked, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { Hero, Section, Sub, Callout, Bold } from "./_shared";

export default function ToolkitPage() {
  return (
    <div data-testid="view-toolkit" className="max-w-5xl">
      <Hero
        moduleNum="06"
        eyebrowLabel="Module"
        icon={Briefcase}
        italic="Outils pratiques"
        accent="& professionnalisme"
        description="Le professionnalisme dans les détails fait souvent la différence entre un intermédiaire qu'on prend au sérieux et un autre qu'on écarte. Voici les outils concrets pour travailler efficacement et projeter une image professionnelle irréprochable."
      />

      <Section eyebrow="Emails" title="Modèles d'emails professionnels" index="6.1">
        <Sub title="Email type 1 : Premier contact avec un vendeur">
          <EmailBlock
            title="Premier contact"
            subject="Inquiry — [Produit] — [Volume] MT/kg — Qualified Buyer Available"
            body={`Dear [Nom],

My name is [Votre Nom], I am a commodity broker based in [Ville/Pays]. I am currently representing a qualified end buyer seeking [Produit] with the following specifications:

• Product: [ex. D6 Virgin Diesel / AU Gold Dore Bars 22+ carats / OPC Cement 42.5R]
• Quantity: [ex. 50,000 MT per month / 500 kg per shipment]
• Delivery: [ex. CIF Rotterdam / CIF Tema Port, Ghana / FOB Supplier's Port]
• Payment: [ex. LC at sight via Top 25 World Bank / MT103 Cash TT / SBLC]
• Duration: [ex. 12 months contract, renewable / Spot trial shipment first]
• Frequency: [ex. Monthly shipments]

If you are a mandate or direct seller and can supply to the above specifications, kindly forward your Full Corporate Offer (FCO) along with proof of product availability (proof of stock, mining license, refinery certificate, or equivalent).

Please be advised that a Non-Circumvention Non-Disclosure Agreement (NCNDA) and an Irrevocable Master Fee Protection Agreement (IMFPA) will be required prior to any principal introduction.

Looking forward to your response.

Kind regards,
[Votre Prénom NOM]
[Titre : Senior Commodity Broker / Trade Consultant]
[Société]
[Email professionnel]
[WhatsApp / Téléphone]`}
          />
        </Sub>

        <Sub title="Email type 2 : Demande de signature NCNDA">
          <EmailBlock
            title="Demande NCNDA"
            subject="NCNDA & IMFPA — [Référence du deal] — Required for Principal Introduction"
            body={`Dear [Nom],

Further to our recent discussions regarding [produit — ex. AU Gold Dore, 500 kg per month], I am pleased to confirm that our buyer has expressed serious interest in proceeding with a transaction based on the terms discussed.

As a prerequisite to any principal introduction, please find attached our standard Non-Circumvention Non-Disclosure Agreement (NCNDA) and Irrevocable Master Fee Protection Agreement (IMFPA) for your review and signature.

These documents are standard procedure in international commodity transactions and serve to protect all parties involved — sellers, buyers, and intermediaries — against circumvention and unauthorized disclosure of confidential information.

Upon receipt of the duly signed documents, we will proceed with the formal introduction of the parties and the full transaction procedure.

Kindly review, sign, and return at your earliest convenience.

Should you require any clarification regarding the documents, please do not hesitate to contact me directly.

Best regards,
[Votre Prénom NOM]
[Société]
[Contact]`}
          />
        </Sub>

        <Sub title="Email type 3 : Relance professionnelle (Follow-Up)">
          <EmailBlock
            title="Follow-Up"
            subject="Follow-Up — [Référence du deal] — Awaiting Your Confirmation"
            body={`Dear [Nom],

I hope this message finds you well.

I am following up on my previous email dated [date], regarding [sujet précis : the FCO for 500 kg AU Gold Dore / the signed NCNDA / the inspection report for the trial shipment].

I have not yet received your response and wanted to ensure my communication reached you correctly.

Please note that our buyer's offer remains valid until [date précise]. Beyond this date, we cannot guarantee the availability of funds or the buyer's current requirements, as their needs may be redirected.

We would greatly appreciate your earliest feedback so we can proceed accordingly and maintain your priority in this transaction.

Thank you for your attention to this matter.

Kind regards,
[Votre Prénom NOM]
[Contact]`}
          />
        </Sub>

        <Sub title="Email type 4 : Retrait professionnel d'un deal">
          <EmailBlock
            title="Retrait"
            subject="Withdrawal — [Référence du deal] — [Produit]"
            body={`Dear [Nom],

Following our recent exchanges regarding [produit et termes], we have conducted an internal review of the transaction parameters and the documentation provided.

After careful consideration, we regret to inform you that we are unable to continue with the current procedure, as the conditions do not meet the due diligence requirements of our principal.

We hereby officially withdraw from this transaction process.

We remain open to future opportunities and wish you every success with your project.

Best regards,
[Votre Prénom NOM]
[Société]`}
          />
        </Sub>
      </Section>

      <Section eyebrow="CRM" title="Gérer ses deals avec un CRM" index="6.2">
        <p>
          Un CRM (Customer Relationship Manager) est indispensable dès que vous gérez plus de 3 deals simultanément. Sans
          un suivi structuré, vous perdrez des opportunités, oublierez des deadlines, et mélangerez les informations de
          différentes contreparties — ce qui peut avoir des conséquences graves.
        </p>

        <Sub title="Outils recommandés selon votre niveau">
          <ul className="space-y-3">
            <li><Bold>Notion (gratuit à 16 $/mois) :</Bold> idéal pour démarrer. Créez une base de données « Deals » avec
            des colonnes : Statut du deal, Produit, Volume, Acheteur (nom codé), Vendeur (nom codé), Documents signés
            (oui/non), Dernière action effectuée, Prochaine action à faire, Date limite. Notion permet aussi de lier les
            deals à des pages de notes et à des contacts.</li>
            <li><Bold>Trello (gratuit) :</Bold> visualisation Kanban très intuitive. Créez des colonnes : Prospection →
            NCNDA signé → LOI/FCO reçue → Due Diligence → Deal en cours → Commission encaissée → Deal mort. Chaque deal
            est une carte que vous faites avancer de colonne en colonne.</li>
            <li><Bold>HubSpot CRM (gratuit) :</Bold> plus complet, avec gestion des contacts, intégration des emails,
            timeline d'activité par deal, et pipeline commercial personnalisable. Idéal si vous avez un flux régulier de
            nouveaux contacts.</li>
            <li><Bold>Pipedrive (environ 25 $/mois) :</Bold> spécialisé dans le suivi commercial B2B. Étapes de pipeline
            entièrement personnalisables. Intégration avec Gmail et Outlook. Très adapté au suivi de deals internationaux
            complexes.</li>
          </ul>
        </Sub>

        <Sub title="Les 8 champs essentiels dans votre suivi de deal">
          <ol className="space-y-2 list-decimal list-inside">
            <li>Référence unique du deal (ex : 2024-AU-GH-001 pour un deal d'or en Ghana en 2024)</li>
            <li>Produit et spécifications détaillées (pureté, grade, certification)</li>
            <li>Volume total et fréquence (mensuel, trimestriel, spot)</li>
            <li>Prix de référence convenu et incoterm</li>
            <li>Statut des documents (liste avec dates de signature : NCNDA signé le X, LOI reçue le Y, IMFPA signé le Z, FCO reçue le W)</li>
            <li>Statut actuel et prochaine étape concrète (ex : « En attente du rapport SGS — à relancer le 15/03 »)</li>
            <li>Commission prévue en montant estimé (en USD ou EUR)</li>
            <li>Date limite de validité de l'offre en cours</li>
          </ol>
        </Sub>
      </Section>

      <Section eyebrow="e-Signature" title="Signature électronique internationale" index="6.3">
        <Sub title="DocuSign">
          <p>
            Le standard mondial de la signature électronique. Reconnu légalement dans plus de 180 pays. Chaque document
            signé génère une <Bold>piste d'audit complète</Bold> (adresses IP, timestamps, identifiants email) qui est
            admissible comme preuve dans la plupart des systèmes judiciaires. Tarif : à partir de 15 $/mois pour 5
            enveloppes. Utilisé par les grandes banques, les fonds d'investissement, et les cabinets d'avocats internationaux.
            C'est la référence à utiliser pour les NCNDA et IMFPA importants.
          </p>
        </Sub>
        <Sub title="Adobe Sign">
          <p>
            Intégré à l'écosystème Adobe (Adobe Acrobat, etc.). Valeur légale en UE sous le règlement <Bold>eIDAS</Bold>,
            aux USA, et dans la plupart des pays. Tarif : à partir de 23 $/mois. Pratique si vous utilisez déjà des outils
            Adobe dans votre workflow.
          </p>
        </Sub>
        <Sub title="PandaDoc">
          <p>
            Idéal pour créer, personnaliser et faire signer des documents commerciaux. Inclut des templates prêts à l'emploi.
            Interface très intuitive. Tarif : à partir de 35 $/mois. Particulièrement adapté à la gestion de NCNDA, IMFPA
            et contrats de commission récurrents.
          </p>
        </Sub>
        <Sub title="HelloSign (Dropbox Sign)">
          <p>
            Interface simple et épurée. Valeur légale dans la plupart des juridictions. Tarif : à partir de 20 $/mois.
            Bonne option pour les volumes modérés.
          </p>
        </Sub>
        <Sub title="SignNow">
          <p>
            Solution très compétitive en prix, à partir de 8 $/mois. Légal dans la plupart des juridictions. Moins connue
            mais fonctionnelle.
          </p>
        </Sub>
        <Callout icon={FileSignature} title="Note importante">
          <p>
            Dans certains pays du Moyen-Orient et en Afrique, des partenaires préfèrent encore les signatures manuscrites
            originales sur des documents physiques, parfois accompagnées d'une apostille. La signature électronique accélère
            les premières étapes de due diligence, mais prévoyez systématiquement la possibilité d'un original physique
            signé pour la documentation finale.
          </p>
        </Callout>
      </Section>

      <Section eyebrow="Image" title="Construire son image professionnelle" index="6.4">
        <Sub title="Votre signature email — exemple">
          <div className="border border-[#D4AF37]/30 bg-[#0c0c0c] p-6 md:p-7 font-mono text-xs">
            <div className="text-white text-sm"><strong>John SMITH</strong> | Senior Commodity Broker</div>
            <div className="text-[#A1A1AA] mt-2">XYZ International Trading Ltd — Registered in [Pays]</div>
            <div className="text-[#A1A1AA]">Direct : +971 50 XXX XXXX | WhatsApp : +971 50 XXX XXXX</div>
            <div className="text-[#A1A1AA]">Email : j.smith@xyz-trading.com | Web : www.xyz-trading.com</div>
            <div className="text-[#D4AF37] mt-3">Specialities : Gold, Oil &amp; Gas, Industrial Metals, Cement</div>
            <div className="mt-4 pt-4 border-t border-white/10 text-[#52525B] italic text-[10px] leading-relaxed">
              CONFIDENTIALITY NOTICE: This email and any attachments are confidential and intended solely for the named
              recipient(s). If received in error, please notify the sender immediately and permanently delete this message.
              Unauthorized disclosure, copying, or distribution is strictly prohibited.
            </div>
          </div>
        </Sub>

        <Sub title="Les 10 règles d'or du professionnalisme">
          <ol className="space-y-3 list-decimal list-inside">
            <li><Bold>Règle 1 :</Bold> Répondez à tous les emails professionnels sous 24 heures maximum, même simplement
            pour accuser réception et indiquer quand vous pourrez répondre en détail. La vitesse de réponse est perçue
            comme un indicateur de fiabilité.</li>
            <li><Bold>Règle 2 :</Bold> N'envoyez jamais de prix, d'offres ou de documents non vérifiés. Chaque erreur
            factuelle entame votre crédibilité de manière souvent irréparable dans un milieu où la réputation voyage vite.</li>
            <li><Bold>Règle 3 :</Bold> Documentez TOUT par écrit. Chaque accord verbal doit être suivi d'un email de
            confirmation dans les heures qui suivent. « Tel qu'évoqué lors de notre appel de ce jour, voici les termes
            convenus... » Cette habitude vous protège et crédibilise votre professionnalisme.</li>
            <li><Bold>Règle 4 :</Bold> Ne gérez jamais plus de 4 à 5 deals actifs simultanément si vous êtes seul. La
            qualité de suivi décline exponentiellement avec le nombre de deals. Mieux vaut 2 deals bien gérés qu'8 deals bâclés.</li>
            <li><Bold>Règle 5 :</Bold> Spécialisez-vous dans 1 ou 2 secteurs maximum au départ. Un intermédiaire spécialisé
            dans l'or et les métaux précieux est infiniment plus crédible qu'un intermédiaire « généraliste » qui prétend
            faire du pétrole, du diamant, du riz et des équipements médicaux. La crédibilité vient de la spécialisation.</li>
            <li><Bold>Règle 6 :</Bold> Construisez votre présence LinkedIn de manière professionnelle. Photo professionnelle,
            titre clair et précis, résumé de votre expertise, expériences vérifiables. Dans le secteur des commodités, les
            contreparties vérifient systématiquement votre profil avant d'avancer.</li>
            <li><Bold>Règle 7 :</Bold> Ne promettez jamais ce que vous ne pouvez pas livrer. Si vous n'êtes pas sûr d'une
            information, dites « Je vais vérifier et reviens vers vous dans 24h » plutôt que d'affirmer quelque chose de
            potentiellement faux. La fiabilité de l'information que vous transmettez est votre principal actif.</li>
            <li><Bold>Règle 8 :</Bold> Mettez à jour vos connaissances régulièrement. Les Incoterms ont été révisés en
            2020 (Incoterms® 2020 — la version précédente datait de 2010). Les réglementations AML/KYC évoluent chaque
            année. Les prix des commodités bougent quotidiennement. Un intermédiaire qui n'est pas à jour perd en crédibilité.</li>
            <li><Bold>Règle 9 :</Bold> Soignez votre présentation lors des vidéoconférences. Fond neutre et épuré (un mur
            blanc ou une bibliothèque rangée), éclairage correct (lumière naturelle de face, ou lampe de bureau), tenue
            professionnelle même depuis chez vous, micro de qualité (le micro intégré d'un ordinateur basique nuit à votre
            image). Ces détails semblent insignifiants mais sont immédiatement perçus par vos contreparties.</li>
            <li><Bold>Règle 10 :</Bold> Protégez votre réputation comme votre actif le plus précieux et le plus fragile.
            Dans le secteur des commodités internationales, les réseaux se parlent. Une réputation prend des années à
            construire et peut se détruire en un seul deal mal géré. Refusez les deals douteux, même quand l'argent est tentant.</li>
          </ol>
        </Sub>
      </Section>

      <Section eyebrow="Ressources" title="Ressources indispensables à bookmarker" index="6.5">
        <Sub title="Prix et marchés">
          <BookmarkList items={[
            { url: "lbma.org.uk", href: "https://lbma.org.uk", label: "Or, argent, platine, palladium (fixing quotidien)" },
            { url: "lme.com", href: "https://lme.com", label: "Métaux industriels (cuivre, aluminium, zinc, nickel, etc.)" },
            { url: "spglobal.com/commodityinsights", href: "https://spglobal.com/commodityinsights", label: "Prix pétrole, gaz, pétrochimie, fret maritime (Platts)" },
            { url: "worldbank.org/commodity-markets", href: "https://worldbank.org/en/research/commodity-markets", label: "Indices de prix des commodités mondiales" },
            { url: "investing.com", href: "https://investing.com", label: "Plateforme gratuite pour suivre les cours en temps réel" },
            { url: "tradingeconomics.com", href: "https://tradingeconomics.com", label: "Données économiques et prix de matières premières par pays" },
          ]} />
        </Sub>

        <Sub title="Réglementaire et juridique">
          <BookmarkList items={[
            { url: "iccwbo.org", href: "https://iccwbo.org", label: "International Chamber of Commerce, Incoterms® officiels, arbitrage, LC" },
            { url: "swift.com", href: "https://swift.com", label: "Vérification des codes BIC/SWIFT des banques" },
            { url: "ofac.treas.gov", href: "https://ofac.treas.gov", label: "Liste américaine des sanctions (OFAC SDN List)" },
            { url: "eeas.europa.eu/topics/sanctions", href: "https://eeas.europa.eu/topics/sanctions", label: "Liste des sanctions de l'Union Européenne" },
            { url: "fatf-gafi.org", href: "https://fatf-gafi.org", label: "GAFI, standards internationaux anti-blanchiment" },
          ]} />
        </Sub>

        <Sub title="Vérification d'entreprises">
          <BookmarkList items={[
            { url: "opencorporates.com", href: "https://opencorporates.com", label: "Registres d'entreprises de 140+ pays" },
            { url: "dnb.com", href: "https://dnb.com", label: "Dun & Bradstreet, rapports d'entreprises" },
            { url: "companieshouse.gov.uk", href: "https://find-and-update.company-information.service.gov.uk", label: "Registre britannique, gratuit et complet" },
            { url: "infogreffe.fr", href: "https://infogreffe.fr", label: "Registre français, Kbis et documents officiels" },
          ]} />
        </Sub>

        <Sub title="Logistique, fret & pierres précieuses">
          <BookmarkList items={[
            { url: "clarksons.com", href: "https://clarksons.com", label: "Données de fret maritime et indices Baltic" },
            { url: "freightos.com", href: "https://freightos.com", label: "Comparateur de fret international" },
            { url: "rappaport.com", href: "https://rapaport.com", label: "Rappaport Diamond Report, référence mondiale des prix de diamants" },
            { url: "gemguide.com", href: "https://gemguide.com", label: "GemGuide pour les pierres colorées" },
          ]} />
        </Sub>
      </Section>
    </div>
  );
}

const EmailBlock = ({ title, subject, body }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* */ }
  };
  return (
    <div className="border border-white/10 bg-[#0c0c0c]">
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-[#D4AF37]" /><h4 className="font-serif text-base text-white">{title}</h4></div>
        <button onClick={copy} data-testid={`copy-email-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-2 px-3 py-1.5 border border-white/15 text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          {copied ? <CheckCircle2 className="h-3 w-3 text-[#25D366]" /> : <Copy className="h-3 w-3" />} {copied ? "Copié" : "Copier"}
        </button>
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Objet</div>
        <div className="font-mono text-sm text-white">{subject}</div>
        <div className="mt-5 pt-4 border-t border-white/5"><pre className="font-mono text-xs text-[#C4C4C7] leading-relaxed whitespace-pre-wrap">{body}</pre></div>
      </div>
    </div>
  );
};

const BookmarkList = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
    {items.map((b) => (
      <a key={b.url} href={b.href} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-4 border border-white/10 bg-[#0c0c0c] p-4 hover:border-[#D4AF37]/50 transition-colors group">
        <div className="flex items-center gap-3 min-w-0">
          <BookMarked className="h-4 w-4 text-[#D4AF37] shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-xs text-[#D4AF37] truncate">{b.url}</div>
            <div className="text-xs text-[#A1A1AA] mt-0.5">{b.label}</div>
          </div>
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-[#A1A1AA] group-hover:text-[#D4AF37] transition-colors shrink-0" />
      </a>
    ))}
  </div>
);
